import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductsList } from '../components/organisms';
import { ProductFilters } from '../components/molecules';
import LoadingSpinner from '../components/atoms/LoadingSpinner';
import {
  useHealthGoals,
  usePrefetchProductDetails,
  useProductCategories,
  useProducts
} from '../hooks/products';

export const ProductsPage: FC = () => {
  const navigate = useNavigate();
  const prefetchProduct = usePrefetchProductDetails();
  
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    search?: string;
    categories?: string[];
    healthGoals?: string[];
    sortBy?: 'name' | 'category';
    sortOrder?: 'asc' | 'desc';
  }>({});

  const { data: categories, isLoading: categoriesLoading } = useProductCategories();
  const { data: healthGoals, isLoading: healthGoalsLoading } = useHealthGoals();
  
  const { data: productsData, isLoading: productsLoading, error } = useProducts({
    ...filters,
    page,
    perPage: 20
  });

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleProductClick = (productId: string) => {
    prefetchProduct(productId);
    navigate(`/products/${productId}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isLoadingInitial = categoriesLoading || healthGoalsLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-2">
            Browse and manage your product catalog
          </p>
        </div>

        {isLoadingInitial ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <ProductFilters
                categories={categories ?? []}
                healthGoals={healthGoals ?? []}
                onFiltersChange={handleFiltersChange}
                initialFilters={filters}
              />
            </div>
            
            <div className="lg:col-span-3">
              <ProductsList
                data={productsData}
                isLoading={productsLoading}
                error={error}
                page={page}
                onPageChange={handlePageChange}
                onProductClick={handleProductClick}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};